/**
 * The order annotations must be PAINTED in — as opposed to the order they were
 * saved in.
 *
 * A paint-bucket fill is a BACKDROP: it meets the ink but must never sit on top
 * of it. Storage order is creation order, so filling an area AFTER drawing in it
 * put the fill over the drawing. On a note that read as strokes vanishing the
 * moment anything re-rendered them; over a PDF, a page-sized fill covered the
 * document underneath and the page looked like it had turned solid black.
 *
 * Painting is the only place this ordering matters — selection, erasing and
 * persistence all key off ids — so it is corrected at paint time rather than by
 * rewriting what the user saved. Every surface that renders annotations has to
 * agree: the read-only overlay uses this, the Fabric editor enforces the same
 * rule on its object list (`sinkFills`), and the server's two renderers share
 * `in_paint_order` in graphmind/domain/annotations.py. KEEP THEM IN SYNC.
 */
export function inPaintOrder<T extends { type: string }>(items: readonly T[]): readonly T[] {
  // Fast path: nothing to reorder unless the list actually MIXES fills with
  // other marks. Worth the check — this runs on every overlay repaint.
  let fills = 0;
  for (const a of items) if (a.type === "fill") fills++;
  if (fills === 0 || fills === items.length) return items;
  // Stable within each group, so two overlapping fills still stack the way they
  // were painted.
  return [...items.filter((a) => a.type === "fill"), ...items.filter((a) => a.type !== "fill")];
}
